import { AxiosInstance } from 'axios';

import { FormName } from '@topconagriculture/feed-types';

import { CreateLotPayload, EditNote, Lot, Movement, Note } from '../../../store/entities/lots/lots.types';
import {
  getDateFormat,
  getDecimalSeparator,
  getThousandSeparator,
  getTimeFormat,
  saveFile,
} from '../../helpers/utils/utils';
import { APIManagerConfig } from '../apiManager';
import { PresetSettings } from '../preset/preset.types';

const userSettings = {
  dateFormat: getDateFormat(),
  timeFormat: getTimeFormat(),
  decimalSeparator: getDecimalSeparator(),
  thousandSeparator: getThousandSeparator(),
};

const LotServiceFactory = ({ HTTPBase, config }: { HTTPBase: AxiosInstance; config: APIManagerConfig }) => {
  return {
    getLots: (includeDetails?: boolean): Promise<Lot[]> =>
      HTTPBase.get(`farm/${config.farmId}/lots`, { params: { includeDetails } }).then((res) => res.data),
    getLotById: (id: string, includeCosts?: boolean): Promise<Lot[]> =>
      HTTPBase.get(`farm/${config.farmId}/lots/${id}`, { params: { includeCosts } }).then((res) => res.data),
    createLot: (data: CreateLotPayload, formName: FormName): Promise<Lot> =>
      HTTPBase.post(`farm/${config.farmId}/lots`, data, { headers: { formName } }).then((res) => res.data),
    createMovement: (lotId: string, movement: Partial<Movement>, formName: FormName): Promise<Movement> =>
      HTTPBase.post(`farm/${config.farmId}/lots/${lotId}/movements`, movement, { headers: { formName } }).then(
        (res) => res.data,
      ),
    updateLot: (lotId: string, data: Partial<Lot>, formName: FormName, nosave = false): Promise<Lot> =>
      HTTPBase.patch(`farm/${config.farmId}/lots/${lotId}?nosave=${nosave}`, data, { headers: { formName } }).then(
        (res) => res.data,
      ),
    createNote: (lotId: string, note: Partial<Note>, formName: FormName): Promise<Note> =>
      HTTPBase.post(`farm/${config.farmId}/lots/${lotId}/notes`, note, { headers: { formName } }).then(
        (res) => res.data,
      ),
    deleteNote: (lotId: string, noteId: string, formName: FormName): Promise<void> =>
      HTTPBase.delete(`farm/${config.farmId}/lots/${lotId}/notes/${noteId}`, { headers: { formName } }).then(
        (res) => res.data,
      ),
    editNote: (lotId: string, data: EditNote, formName: FormName): Promise<void> =>
      HTTPBase.patch(`farm/${config.farmId}/lots/${lotId}/notes/${data.id}`, data, { headers: { formName } }).then(
        (res) => res.data,
      ),
    getCloseoutReport: (lotId: string, data?: any): Promise<any> =>
      HTTPBase.post(`farm/${config.farmId}/reports/lotcloseout/${lotId}/report`, data, {
        headers: { Accept: 'application/pdf' },
        responseType: 'blob',
      }).then((res) => res.data.arrayBuffer().then((r) => new Uint8Array(r))),
    downloadCloseoutReport: (lotId: string, filename: string, data?: any): Promise<any> =>
      HTTPBase.post(`farm/${config.farmId}/reports/lotcloseout/${lotId}/report`, data, {
        headers: { Accept: 'application/pdf' },
        responseType: 'blob',
      }).then((res) => saveFile(res, 'application/pdf', 'pdf', filename)),
    deleteLot: (lotId: string, formName: FormName): Promise<void> =>
      HTTPBase.delete(`farm/${config.farmId}/lots/${lotId}`, { headers: { formName } }).then((res) => res.data),
    getCostSummaryReport: (lotId: string, preset) =>
      HTTPBase.post(`farm/${config.farmId}/reports/lotcost/${lotId}/data`, preset).then((res) => res.data),
    downloadCostSummaryReport: (
      format: string,
      ext: string,
      preset: PresetSettings,
      fileName: string,
      lotId: string,
    ): Promise<void> => {
      return HTTPBase.post(
        `farm/${config.farmId}/reports/lotcost/${lotId}/report`,
        {
          preset,
        },
        {
          headers: { Accept: format },
          responseType: 'blob',
        },
      ).then((res) => saveFile(res, format, ext, fileName));
    },
    getCattleMovementsReport: (preset: PresetSettings) =>
      HTTPBase.post(`farm/${config.farmId}/reports/cattlemovements/data`, { preset, userSettings }).then(
        (res) => res.data,
      ),
  };
};

export default LotServiceFactory;
